import {
  AbilityBuilder,
  createMongoAbility,
  type ForcedSubject,
  type MongoAbility,
} from "@casl/ability";

import { ruleConditionsFor } from "./ability-rule-conditions";
import { COGNITO_ALL_PROFILE_GROUPS, COGNITO_AUTOMATION_GROUP } from "./auth-constants";
import { defineAutomationRules } from "./automation-ability-rules";
import { defineLegalRules, defineNegotiatorRules } from "./case-staff-ability-rules";
import {
  defineCustomerRules,
  defineExpertRules,
  definePartnerRules,
} from "./external-ability-rules";
import { defineAdminRules, defineManagerRules } from "./management-ability-rules";
import { defineOperatorRules } from "./operator-ability-rules";

type CaseSubject = ForcedSubject<"Case"> & {
  id: string;
  userId?: string;
  clientId?: string;
  branchId?: string | null;
  step?: string;
  caseType?: string;
};
type ClientSubject = ForcedSubject<"Client"> & { id: string };
type DocumentSubject = ForcedSubject<"Document"> & {
  clientId?: string;
  branchId?: string | null;
  caseId?: string | null;
  uploadedBy?: string;
  visibleTo?: readonly string[];
};
type TaskSubject = ForcedSubject<"Task"> & {
  assignedTo?: string | null;
  contextType?: "case" | "client";
  contextId?: string;
  "createdBy.id"?: string;
};

export type AppAction =
  | "manage"
  | "create"
  | "read"
  | "update"
  | "delete"
  | "changeStep"
  | "transitionToLegalProcess"
  | "analyze"
  | "startAnalysis"
  | "reviewAnalysis"
  | "markSectionReviewed"
  | "overrideReviewedAnalysis"
  | "accessAnalysisReview"
  | "finishOthersDraft"
  | "manageNotes"
  | "manageOtherNotes"
  | "manageConversations"
  | "viewHistory"
  | "deleteContacts"
  | "bypassTaskOwnership"
  | "executeUnowned"
  | "cancel"
  | "reopen"
  | "execute"
  | "assignOnCreate"
  | "createPrimary"
  | "readAll"
  | "readPrivate"
  | "readAllBranches"
  | "updateMetadata"
  | "manageDeadline"
  | "createReminder"
  | "emailAssignee"
  | "updateReminder"
  | "removeReminder"
  | "waiveRequirements"
  | "readAnalysis"
  | "broadcast"
  | "resolveCaseException";

type AppSubjectName =
  | "all"
  | "Case"
  | "Client"
  | "Task"
  | "Document"
  | "Profile"
  | "User"
  | "AuditLog"
  | "Automation"
  | "TaskSettings"
  | "Dashboard"
  | "Report"
  | "Notification"
  | "UserPreferences"
  | "Financial";

type AppSubject = AppSubjectName | CaseSubject | ClientSubject | DocumentSubject | TaskSubject;

export type AppAbility = MongoAbility<[AppAction, AppSubject]>;
export type AppPermission = Parameters<AppAbility["can"]>;

export type AbilityContext = {
  groups: readonly string[];
  userId?: string;
  username?: string;
  clientIds?: readonly string[];
  branchIds?: readonly string[];
  assignedCaseIds?: readonly string[];
  /**
   * Case ids whose fields the caller may currently edit, resolved from the
   * Case `update` rules before this ability is built. Reaches documents
   * attached to those cases for roles without a blanket document grant.
   */
  editableCaseIds?: readonly string[];
  caseId?: string;
  hasOpenCaseReviewTask?: boolean;
  hasAnalysisReviewTask?: boolean;
};

export const APPLICATION_ROLES = [...COGNITO_ALL_PROFILE_GROUPS, COGNITO_AUTOMATION_GROUP] as const;

export const defineAbilityFor = (context: AbilityContext): AppAbility => {
  const { can, cannot, build } = new AbilityBuilder<AppAbility>(createMongoAbility);
  const conditions = ruleConditionsFor(context);

  if (context.groups.includes("Admin")) defineAdminRules(can, cannot, conditions);
  if (context.groups.includes("Manager")) defineManagerRules(can, conditions);
  if (context.groups.includes("Negotiator")) defineNegotiatorRules(can, conditions);
  if (context.groups.includes("Legal")) defineLegalRules(can, conditions);
  if (context.groups.includes("Operator")) defineOperatorRules(can, conditions);
  if (context.groups.includes("Customer")) defineCustomerRules(can, conditions, context.clientIds);
  if (context.groups.includes("Partner") && !context.groups.includes("Customer")) {
    definePartnerRules(can, conditions);
  }
  if (context.groups.includes("Expert")) defineExpertRules(can, conditions);
  if (context.groups.includes("Automation")) defineAutomationRules(can, conditions);

  return build();
};

export const clientOrBranchCondition = (
  clientIds: readonly string[] = [],
  branchIds: readonly string[] = [],
): object =>
  branchIds.length === 0
    ? { clientId: { $in: [...clientIds] } }
    : { $or: [{ clientId: { $in: [...clientIds] } }, { branchId: { $in: [...branchIds] } }] };
